import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import { refreshAuthToken } from "../../common/api-services/authenticationApi";
import { AUTH_CONSTANTS } from "./authConstants";
import {removeCookie, setCookie} from "../../../utils/setCookie";

enum RefreshStatus {
  ACTIVE = 'active',
  MAX_RETRIES_REACHED = 'max_retries_reached',
}

const TOKEN_ISSUE_TIME_KEY = 'token_issue_time';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let refreshStatus: RefreshStatus = RefreshStatus.ACTIVE;
let isRedirecting = false;

const redirectOnce = (redirectUrl: string): void => {
  if (isRedirecting) return;
  isRedirecting = true;
  window.location.replace(redirectUrl);
};

const getTokenIssueTime = (): number | null => {
  const timestamp = localStorage.getItem(TOKEN_ISSUE_TIME_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

const setTokenIssueTime = (): void => {
  localStorage.setItem(TOKEN_ISSUE_TIME_KEY, Date.now().toString());
};

const clearTokenIssueTime = (): void => {
  localStorage.removeItem(TOKEN_ISSUE_TIME_KEY);
};

const initializeTokenIssueTime = (): void => {
  const accessToken = Cookies.get("auth_token");
  if (accessToken && !getTokenIssueTime()) {
    setTokenIssueTime();
  }
};
export const setTokenIssuedAt= (): void => {
  setTokenIssueTime();
};

export const isAccessTokenExpired = (token?: string): boolean => {
  const tokenToCheck = token || Cookies.get("auth_token");
  if (!tokenToCheck) return true;

  try {
    const decoded: any = jwt_decode(tokenToCheck);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

const isPasswordExpiredFromToken = (): boolean => {
  const token = Cookies.get("auth_token");
  if (!token) return false;
  try {
    const decoded: any = jwt_decode(token);
    const pwdExpiresAt = decoded?.pwd_expires_at;
    if (!pwdExpiresAt) return false;
    return Date.now() / 1000 > pwdExpiresAt;
  } catch {
    return false;
  }
};

export const getSecondsUntilPasswordExpiry = (): number | null => {
  const token = Cookies.get("auth_token");
  if (!token) return null;
  try {
    const decoded: any = jwt_decode(token);
    const pwdExpiresAt = decoded?.pwd_expires_at;
    if (!pwdExpiresAt) return null;
    return pwdExpiresAt - Date.now() / 1000;
  } catch {
    return null;
  }
};

export const refreshAccessToken = (): Promise<string | null> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = performTokenRefresh().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
  return refreshPromise;
};

// @ts-ignore
const performTokenRefresh = async (): Promise<string | null> => {
  const refreshToken = Cookies.get("refresh_token");

  if (!refreshToken) {
    console.error("No refresh token available");
    return null;
  }

  if (refreshStatus !== RefreshStatus.ACTIVE) {
    return null;
  }

  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Token refresh timeout')), AUTH_CONSTANTS.TOKEN_REFRESH_TIMEOUT_MS);
    });

    const response = await Promise.race([refreshAuthToken(refreshToken), timeoutPromise]) as any;

    if (response?.data?.accessToken && response?.data?.refreshToken) {
      const accessTokenExpiry = new Date(Date.now() + (AUTH_CONSTANTS.ACCESS_TOKEN_EXPIRY_MINUTES * 60 * 1000));
      const refreshTokenExpiry = new Date(Date.now() + (AUTH_CONSTANTS.REFRESH_TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000));

      setCookie("auth_token", response.data.accessToken, { expires: accessTokenExpiry });
      setCookie("refresh_token", response.data.refreshToken, { expires: refreshTokenExpiry });
      setTokenIssueTime();

      return response.data.accessToken;
    } else {
      // removeCookie("auth_token");
      // removeCookie("refresh_token");
      // clearTokenIssueTime();
      // refreshStatus = RefreshStatus.MAX_RETRIES_REACHED;
      // redirectOnce('/sign-in');
      throw new Error("Invalid response from refresh token endpoint");
    }

  } catch (error: any) {
    console.error('Token refresh failed:', error);

    const status = error?.response?.status;

    if (status === 401 || status === 400 || (status && status >= 500) || error?.message?.includes('timeout')) {
      return null;
    }

    return null;
  }

};

export const useRefreshToken = () => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTokens = (): void => {
    removeCookie("auth_token");
    removeCookie("refresh_token");
    clearTokenIssueTime();
  };
  const isAuthenticated = (): boolean => {
    const accessToken = Cookies.get("auth_token");
    const refreshToken = Cookies.get("refresh_token");

    if (!accessToken || !refreshToken) return false;

    return true;
  };

  const handleRefreshFailure = (): void => {
    clearTokens();
    window.location.href = '/sign-in';
  };

  useEffect(() => {
    initializeTokenIssueTime();

    const scheduleNextRefreshCheck = (delayMs?: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (delayMs === undefined) {
        const tokenIssueTime = getTokenIssueTime();
        if (tokenIssueTime) {
          const timeSinceLastRefresh = Date.now() - tokenIssueTime;
          const refreshIntervalMs = AUTH_CONSTANTS.TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000;
          delayMs = Math.max(0, refreshIntervalMs - timeSinceLastRefresh);
        } else {
          delayMs = AUTH_CONSTANTS.TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000;
        }
      }

      if (delayMs > 0) {
        timeoutRef.current = setTimeout(() => {
          checkAndRefreshToken();
        }, delayMs);
      }
    };

    const checkAndRefreshToken = async () => {
      const accessToken = Cookies.get("auth_token");
      const refreshToken = Cookies.get("refresh_token");

      if (!accessToken || !refreshToken) {
        return;
      }

      if (isPasswordExpiredFromToken()) {
        clearTokens();
        redirectOnce('/sign-in');
        return;
      }
      if (!getTokenIssueTime()) {
        setTokenIssueTime();
        return;
      }

      const tokenIssueTime = getTokenIssueTime();
      if (!tokenIssueTime) {
        return;
      }
      const timeSinceLastRefresh = Date.now() - tokenIssueTime;
      const refreshIntervalMs = AUTH_CONSTANTS.TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000;
      if (timeSinceLastRefresh >= refreshIntervalMs) {
        if (isRefreshing) {
          return;
        }
        try {
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            scheduleNextRefreshCheck();
          } else {
            console.warn('Token refresh failed, keeping previous tokens');
            scheduleNextRefreshCheck();
          }
        } catch (error) {
          console.error('Error during token refresh:', error);
          scheduleNextRefreshCheck();
        }
      } else {
        const remainingMs = refreshIntervalMs - timeSinceLastRefresh;
        scheduleNextRefreshCheck(remainingMs);
      }
    };
    const checkIntervalMs = 60 * 1000;
    intervalRef.current = setInterval(checkAndRefreshToken, checkIntervalMs);
    checkAndRefreshToken();
    scheduleNextRefreshCheck();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isAccessTokenExpired,
    refreshAccessToken,
    clearTokens,
    isAuthenticated,
    handleRefreshFailure,
  };
};
